import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import usePrintLocale from 'pages/Shipments/PrintOrders/usePrintLocale';

import AppLogger from 'utils/AppLogger';

import { faXmark } from '@fortawesome/pro-solid-svg-icons';

import { setPrintLocale } from './PrintSettingsSlice';
import AWBTemplateForm from './components/forms/AWBTemplateForm';
import BranchLabelTemplateForm from './components/forms/BranchLabelTemplateForm';
import InvoiceTemplateForm from './components/forms/InvoiceTemplateForm';
import { defaultAwbConfigValues, defaultBranchConfigValues, defaultInvoiceConfigValues } from './static/templateConfig';

const PrintTemplateForm = NiceModal.create((props) => {
	const { templateName, templateType, formMethods, fieldName, onSubmit } = props;

	const { setValue, control, handleSubmit, resetField, watch } = formMethods;
	const { t } = useTranslation();
	const modal = useModal();
	const { fetchLanguage } = usePrintLocale();
	const [isLanguageFetched, setIsLanguageFetched] = useState(false);
	const dispatch = useDispatch();

	const formTypes = useMemo(
		() => ({
			invoicePrintPropJSON: InvoiceTemplateForm,
			branchPrintPropJSON: BranchLabelTemplateForm,
			awbPrintPropJSON: AWBTemplateForm
		}),
		[]
	);

	const defaultValueMap = useMemo(
		() => ({
			invoicePrintPropJSON: defaultInvoiceConfigValues,
			branchPrintPropJSON: defaultBranchConfigValues,
			awbPrintPropJSON: defaultAwbConfigValues
		}),
		[]
	);

	const { isDirty } = useFormState({ control });

	const handleResetDefaultValues = (fieldName) => {
		setValue(fieldName, defaultValueMap[fieldName], {
			shouldValidate: true,
			shouldDirty: true
		});
	};

	const handleCloseModal = useCallback(() => {
		if (isDirty) {
			NiceModal.show(ConfirmationModal, {
				title: 'youHaveUnsavedChanges',
				subTitle: 'areYouSureYouWantToLeave',
				yesAction: () => {
					resetField(fieldName);
					modal.remove();
				}
			});
		} else {
			modal.remove();
		}
	}, [modal, isDirty, templateName]);

	const fetchLangResource = useCallback(async (templateName, language) => {
		if (!language) return;
		if (!templateName) return;
		setIsLanguageFetched(false);
		try {
			if (templateName === 'template2') {
				await fetchLanguage([...new Set(['arabicValue', language])]);
			} else if (templateName === 'template5') {
				await fetchLanguage([...new Set(['arabicValue', 'engValue', language])]);
			} else {
				await fetchLanguage(language);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLanguageFetched(true);
		}
	}, []);

	const templateNameWatcher = watch(`${fieldName}.templateName`);
	const languageWatcher = watch(`${fieldName}.language`);

	// EFFECTS
	useEffect(() => {
		fetchLangResource(templateNameWatcher, languageWatcher).then(() => {
			dispatch(setPrintLocale(languageWatcher));
		});
	}, [templateNameWatcher, languageWatcher]);

	return (
		<Modal maxWidth="xx-large" fullwidth className="modal-printTemplateForm" closeButton={false}>
			<ModalTitle
				style={{
					display: 'flex',
					justifyContent: 'space-between'
				}}>
				<Typography variant="p1" style={{ flex: '0 0 90%' }}>
					{t('edit')}&nbsp;{t(templateType)} - {t(templateNameWatcher)}
				</Typography>
				<Button
					className="closeBtn"
					variant="borderless"
					icon={faXmark}
					onClick={handleCloseModal}
					iconSize="medium"
					iconButton
				/>
			</ModalTitle>
			<ModalContent>
				{formTypes[fieldName] &&
					createElement(formTypes[fieldName], {
						key: fieldName,
						isLanguageFetched,
						formMethods,
						fieldName,
						templateName,
						propJSON: watch(fieldName),
						onSubmit
					})}
			</ModalContent>
			{formTypes[fieldName] && (
				<ModalActions>
					<Button
						label="print.resetSettings"
						variant="outline"
						onClick={() => handleResetDefaultValues(fieldName)}
					/>
					<Button
						label="save"
						onClick={() => {
							handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})().then(() => {
								modal.remove();
							});
						}}
						disabled={!isDirty}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default PrintTemplateForm;
